<template>
  <div class="spl-vue-container">
    <div class="selections-and-help">
      <div class="selections">
        <DropDown v-model="selectedSite" :options="availableSites" placeholder="Select Site" class="dropdown-site"
          @click="fetchAvailableSites"></DropDown>
        <DropDown v-model="selectedUnit" :options="availableUnits" optionLabel="unit" optionValue="unit"
          :optionDisabled="(u) => !u.is_enabled" placeholder="Unit..." class="dropdown-unit">
        </DropDown>

        <div class="flex-auto">
          <DatePicker id="calendar-24h" v-model="date" showTime hourFormat="24" showIcon :stepMinute="Number(15)"
            dateFormat="dd/mm/yy" />
        </div>
        <button @click="handleSubmit" class="item-button">Request Data</button>
      </div>
      <div class="button-group">
        <button v-if="receivedResponse && stage === 'factsheet'" :class="[
          'item-button',
          {
            loading: generateReportState === 'loading',
            error: generateReportState === 'error',
          },
        ]" @click="showReportModal = true">
          Generate Report
        </button>
        <button v-if="receivedResponse" :class="[
          'item-button',
          {
            loading: requestExpertState === 'loading',
            error: requestExpertState === 'error',
          },
        ]" @click="showRequestExpertModal = true">
          Request Expert
        </button>
        <button class="icon-button" @click="showControlsMenu = !showControlsMenu">
          <img v-if="showControlsMenu" src="@/assets/png/cogwheel_arrow_up.png" class="icon-image" />
          <img v-else src="@/assets/png/cogwheel_arrow_down.png" class="icon-image" />
        </button>
        <div v-if="showControlsMenu" class="controls-menu">
          <div class="menu-section">
            <label>Band-pass filter</label>
            <DoubleSlider v-model="range" :min="0" :max="6300" :choices="octaveBands" />
          </div>
          <div class="ml-toggle">
            <label class="switch-row" :title="'Show/hide ML prediction overlays'">
              <input type="checkbox" v-model="showMlPredictions" @change="updateMlContribOptions" />
              ML Predictions
            </label>
          </div>
          <!-- If we're going to hide this until we can work out the ML access
                instead of removing it, we just make it dependent on ML predictions being active
                which would make for better ux anyway.
             -->
        </div>
      </div>
    </div>

    <div class="info-bar">
      <div class="status-container" v-if="receivedResponse">
        <!-- FTP Status -->
        <div class="status-item">
          <div class="status-item-icon">
            <span v-if="ftpStatus === 'good'" class="icon available">&#10004;</span>
            <span v-if="ftpStatus === 'offline'" class="icon unavailable">&#10006;</span>
            <span v-if="ftpStatus === 'unavailable'" class="icon icon-warning">&#9888;</span>
            <div v-if="ftpStatus === null" class="lds-dual-ring"></div>
          </div>
          <span>FTP Status</span>
          <span class="tooltip-icon" :title="ftpStatusMsg">&#9432;</span>
        </div>

        <!-- Audio Status -->
        <div class="status-item">
          <div class="status-item-icon">
            <span v-if="audioStatus === 'good'" class="icon available">&#10004;</span>
            <span v-if="audioStatus === 'missing'" class="icon unavailable">&#10006;</span>
            <div v-if="audioStatus === null" class="lds-dual-ring"></div>
          </div>
          Audio Quality
          <span class="tooltip-icon" :title="audioStatusMsg">&#9432;</span>
        </div>

        <!-- ML Predictions Status -->
        <div class="status-item" v-if="showMlPredictions">
          <div class="status-item-icon">
            <span v-if="mlStatus === 'finished'" class="icon available">&#10004;</span>
            <span v-if="mlStatus === 'error' || mlStatus === 'apiServerDown'" class="icon unavailable">&#10006;</span>
            <div v-if="mlStatus === 'loading'" class="lds-dual-ring"></div>
          </div>
          <span>ML Predictions</span>
        </div>
      </div>

      <div class="info-bar-right">
        <div v-if="lastEditedBy">
          Last Edits By: {{ formatDisplayName(lastEditedBy) }}
        </div>
        <div class="status-item controls-dropdown" v-if="
          receivedResponse &&
          (audioStatus === 'good' || audioStatus === 'missing')
        ">
        </div>
      </div>
    </div>

    <div class="sound-levels-page-container" @mouseup="pageMouseUpHanlder" v-if="
      receivedResponse &&
      (audioStatus === 'good' || audioStatus === 'missing data')
    ">
      <div class="sound-levels-app-container">
        <div class="pages-wrapper">
          <!----- Analysis page ----->
          <div class="analysis-page" :class="{ active: stage === 'analysis' }">
            <!-- <select v-model.number="currentRegion" @change="updateSelectedFeedback">
              <option v-for="(value, region) in regions" :key="region" :value="region">
                {{ region }}
              </option>
            </select>

            <button @click="createNewRegion">Add new region</button>
            <button @click="unselectAllInRegion">Unselect all</button>
            <button @click="selectAllInRegion">Select all</button> -->

            <!-- Plot container -->
            <div class="sound-levels-predictions-and-audio-container">
              <!-- <p class="plot-class">{{ lifetimeDatas.class }}</p> -->
              <!-- Wrapper div to capture mouse events -->
              <div class="chart-container" @mousedown="handleChartMouseDown" @mousemove="handleChartMouseMove"
                @mouseup="handleChartMouseUp"
                :style="`width: 100%; min-height: 200px; position: relative; visibility: ${getSplVisibility()};`">
                <v-chart :option="timeseriesChartOptions" ref="splChartRef" autoresize @datazoom="handleDataZoomEvent"
                  @legendselectchanged="handleLegendSelectChanged" />
                <div v-if="mousedownFeedback.visible" class="mousedown-feedback" :style="{
                  left: mousedownFeedback.x + 'px',
                  top: mousedownFeedback.y + 'px',
                }"></div>
                <div v-if="serverLoading" class="whiteout-overlay">
                  <img src="@/assets/svg/12-dots-scale-rotate-magecdn.svg" alt="Loading..." class="spl-spinner" />
                </div>
              </div>
              <div class="spectra-chart-container-spl" :style="`visibility: ${getSpectraChartVisibility()};`">
                <!-- <v-chart
                  :option="spectraChartOptions"
                  ref="spectraChartRef"
                  autoresize
                /> -->
                <WeightedSpectraChart :APIparams="APIparams" />
              </div>

              <!-- Audio Playback -->
              <!-- <p class="plot-class">Audio Playback</p> -->
              <!-- Pass the sharedZoom using v-model so that changes from the child update the parent's value -->
              <AudioPlayer ref="audioPlayer" :APIparams="APIparams" v-model:sharedZoom="sharedZoom"
                v-model:audioTime="audioTime" v-model:chartView="chartView" :visible="stage === 'analysis'" />
            </div>

            <div class="spl-analysis">
              <!-- Interactive Overlay -->
              <!-- Note: we now pass the region-specific formula map -->
              <InteractiveOverlay ref="overlay" v-if="backendResponse" :backendResponse="backendResponse"
                :selectedTimestamps="selectedTimestamps" :mlResponse="mlResponse" :currentRange="range"
                :selectable_sources="selectable_sources" :octaveBands="octaveBands" :regions="regions"
                :regionColours="regionColours" :selectedRegion="currentRegion" :APIparams="APIparams"
                :weightedSpectra="weightedSpectra" @create-region="createNewRegion"
                @delete-region="(regionName) => deleteRegion(regionName)" @delete-source="
                  (regionName, sourceName) =>
                    deleteSource(regionName, sourceName)
                " @formula-changed="
                  (sourcesMap) => handleFormulaChange(sourcesMap)
                " @select-region="(region) => selectRegion(region)" />
              <!--
                @add-new-source="(regionName, source) => addNewSource(regionName, source)"

                :formulaMap="regionFormulaMap[currentRegion] || {}"-->
            </div>

            <div class="page-selection">
              <button @click="createNewRegion" class="item-button">Add New Source Annotation</button>
              <button v-if="stage === 'analysis'" @click="setStage('factsheet')"
                :disabled="serverLoading || !weightedSpectra || Object.keys(weightedSpectra).length === 0"
                class="item-button">
                Calculate Source Levels
              </button>
            </div>
          </div>

          <!----- Fact sheet C page ----->
          <div v-if="stage === 'factsheet'" class="factsheet-page" :class="{ active: stage === 'factsheet' }">

            <FactSheetCDashboard :APIparams="APIparams" :weightedSpectra="weightedSpectra"
              :selectable_sources="selectable_sources" :formulaMap="regionFormulaMap" :regionMap="regions"
              :sourcesMap="sourcesMap" :ml_predictions="mlResponse?.predictions"
              :selectedSources="getSelectedSourcesFromAnalysis()" />

            <div class="page-selection">
              <button v-if="stage === 'factsheet'" @click="setStage('analysis')" class="item-button">
                Return to Analysis
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <GenerateReportModal v-if="showReportModal" ref="reportModal" :event_datetime="APIparams.date_time"
      :unit="selectedUnit" :site="selectedSite" :profile="profile" :sourcesMap="sourcesMap" :regionMap="regions"
      :weightedSpectra="weightedSpectra" @close="showReportModal = false" @submit_report="handleReportSubmit" />
    <UnsavedChangesModal v-if="showUnsavedModal" @yes="confirmUnsavedYes" @no="confirmUnsavedNo"
      @cancel="confirmUnsavedCancel" />
    <RequestExpertModal v-if="showRequestExpertModal" :profile="profile" :site="selectedSite" :unit="selectedUnit"
      :datetime="APIparams.date_time" @close="showRequestExpertModal = false" @submit="handleExpertSubmit" />
  </div>
</template>

<script>
import { ref, nextTick } from "vue";
import axiosInstance from "@/axios-instance";
import AudioPlayer from "./AudioPlayer.vue";
import DoubleSlider from "./DoubleSlider.vue";
import InteractiveOverlay from "./InteractiveOverlay.vue";
import FactSheetCDashboard from "./FactSheetCDashboard.vue";
import WeightedSpectraChart from "./WeightedSpectraChart.vue";
import GenerateReportModal from "./GenerateReportModal.vue";
import UnsavedChangesModal from "./UnsavedChangesModal.vue";
import RequestExpertModal from "./RequestExpertModal.vue";
// import InvalidInputPopup from "./InvalidInputPopup.vue"
import { getToken } from "../keycloak-wrapper.ts";

// Import ECharts and the Vue wrapper.
import VChart from "vue-echarts";
import { bankersRound } from "../helpers.ts";

const FILTERED_LEVELS_NAME = "Filtered Levels";

export default {
  name: "SoundPressureLevels",
  components: {
    VChart,
    AudioPlayer,
    DoubleSlider,
    InteractiveOverlay,
    FactSheetCDashboard,
    WeightedSpectraChart,
    GenerateReportModal,
    UnsavedChangesModal,
    RequestExpertModal,
  },
  props: {
    initialParams: {
      type: Object,
    },
    profile: { type: Object, required: true },
  },
  data() {
    return {
      finishedMount: false,
      handlingSubmit: false,
      showMlPredictions: true,
      showReportModal: false,
      showUnsavedModal: false,
      showRequestExpertModal: false,
      unsavedModalResolve: null,
      availableSites: [],
      selectedSite: "",
      availableUnits: [],
      selectedUnit: "",
      cachedData: {},
      showInvalidInputWindow: false,
      invalidInputPopupMessage:
        "Please Select a Site, Unit, and Datetime before pressing Submit.",
      apiResponse: null,
      receivedResponse: false,
      showControlsMenu: false,
      generateReportState: "ready", // 'ready' | 'loading' | 'error'
      predictionSeriesNames: [],
      seriesVisibility: {},
      serverLoading: false,
      lifetimeDatas: {
        class: "Sound Levels (dBA)",
      },
      sourcesMap: [],
      octaveBands: [
        6.3, 8.0, 10.0, 12.5, 16.0, 20.0, 25.0, 31.5, 40.0, 50.0, 63.0, 80.0,
        100.0, 125.0, 160.0, 200.0, 250.0, 315.0, 400.0, 500.0, 630.0, 800.0,
        1000.0, 1250.0, 1600.0, 2000.0, 2500.0, 3150.0, 4000.0, 5000.0, 6300.0,
      ],
      backendLoading: true,
      mlStatus: null,
      lastPreparedAtBySite: {},
      backendResponse: null,
      mlResponse: null,
      xArray: [],
      sharedZoom: {
        minTime: 0,
        maxTime: 0,
        minIndex: 0,
        maxIndex: 100,
      },
      audioTime: null,
      interactiveBoxVisible: false,
      interactiveData: [],
      selectedTimestamps: [],
      lastSelectedTimestamp: null,
      isDragging: false,
      isZooming: false,
      dataZoomEvent: null,
      dragStartX: null,
      dragStartY: null,
      dragLastIndex: null,
      dragDirection: 0,
      dragEndX: null,
      dragFlag: true,
      dragMode: "selecting",
      extendEdge: null,
      regions: {},
      currentDragRegionRange: null,
      currentRegion: 0,
      regionCounter: 0,
      regionColours: [
        "rgba(230, 159, 0, 0.2)",
        "rgba(86, 180, 233, 0.2)",
        "rgba(0, 158, 115, 0.2)",
        "rgba(240, 228, 66, 0.2)",
        "rgba(0, 114, 178, 0.2)",
        "rgba(213, 94, 0, 0.2)",
        "rgba(204, 121, 167, 0.2)",
      ],
      currentDragSelection: new Set(),
      dragStartTimeValue: null,
      initialSelectionMapSnapshot: {},
      initialRegionSnapshot: [],
      dragStartTimestamp: null,
      newSource: "Other",
      valid_sources: ["Mine Noise", "Insects", "Birds", "...loading others"],
      displayed_sources: [],
      selectable_sources: [],
      selectionDeterminedValue: 0,
      range: [20, 630],
      filteredTrace: null,
      timeseriesChartOptions: {},
      lineSeries: null, // for timeSeries chart lines - separate from markAreas
      spectraChartOptions: {},
      mousedownFeedback: {
        visible: false,
        x: 0,
        y: 0,
      },
      stage: "analysis",
      // Remove the global formulaMap in favor of a per-region storage.
      regionFormulaMap: {},
      chartView: "Timeseries",
      weightedSpectra: {},
      audioStatus: null,
      audioStatusMsg: " ",
      ftpStatus: null,
      ftpStatusMsg: " ",
      APIparams: {
        site: null,
        unit_ID: 100,
        date_time: this.roundToNearest15Min(
          new Date(new Date() - new Date().getTimezoneOffset() * 60000)
        ).toISOString(),
        selected_times: null,
      },
      date: ref(this.roundToNearest15Min(new Date(new Date()))),
      abortController: null,
      requestExpertState: "ready",
      lastEditedBy: null,
      includeUnaccounted: true, // controls whether the "Unaccounted for Noise" series is used
      overlayPredictions: true, // switch between % stack vs LAeq overlays
      normalizePercentages: true, // Normalizes ML analysis under the TOT A line
      areaOrderMode: "default", // 'default' | 'largest-first' | 'smallest-first' | 'alphabetical'
      seriesColorMap: {}, // { [seriesName]: '#RRGGBB' }
      colorPalette: [
        "#1f77b4",
        "#ff7f0e",
        "#2ca02c",
        "#d62728",
        "#9467bd",
        "#8c564b",
        "#e377c2",
        "#7f7f7f",
        "#bcbd22",
        "#17becf",
      ],
      pinnedSeriesNames: new Set(["TOT A", FILTERED_LEVELS_NAME, "Unaccounted for Noise"]),
      hatchPatternCanvas: null,
    };
  },
  methods: {
    closePopup() {
      this.showInvalidInputWindow = false;
    },
    formatDisplayName(email) {
      if (!email) {
        return email;
      }
      const localPart = email.split("@")[0];
      // split on dots, capitalize each piece, then join with spaces
      return localPart
        .split(".")
        .map(
          (name) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
        )
        .join(" ");
    },
    async computeFtpBufferMsg() {
      switch (this.ftpStatus) {
        case "good":
          this.ftpStatusMsg =
            "FTP connection is online and target directory exists";
          return;
        case "offline":
          this.ftpStatusMsg = "FTP connection is offline!";
          return;
        case "unavailable":
          this.ftpStatusMsg =
            "FTP connection is online but the target directory cannot be found.";
          return;
        case "error":
          return "Buffer has encountered an error.";
        default:
          return "Unknown buffer status.";
      }
    },
    async handleSubmit() {
      if (
        this.regions &&
        (Object.keys(this.regionFormulaMap).length > 1 ||
          (this.regionFormulaMap[this.currentRegion] &&
            Object.keys(this.regionFormulaMap[this.currentRegion]).length !==
            0))
      ) {
        const cancelled = await this.handleUnmount();
        if (cancelled) return;
      }
      this.serverLoading = true;
      this.setStage("analysis");
      if (this.abortController) {
        console.log("aborting!");
        await nextTick();
        await this.abortController.abort();
        await nextTick();
      }
      this.handlingSubmit = true;

      this.abortController = new AbortController();

      this.receivedResponse = false;

      this.ftpStatus = null;
      this.mlStatus = null;
      this.serverLoading = true;
      this.date = this.roundToNearest15Min(this.date);
      this.backendResponse = null;
      this.weightedSpectra = {};
      this.regions = {};
      this.regionFormulaMap = {};
      this.mlResponse = null;
      this.xArray = [];
      this.regionCounter = 0;
      this.currentRegion = 0;
      this.selectedTimestamps = [];
      this.lastSelectedTimestamp = null;
      this.timeseriesChartOptions = {};
      this.lineSeries = {};
      this.spectraChartOptions = {};
      this.lastEditedBy = null;
      if (!this.selectedUnit || !this.selectedSite || !this.date) {
        this.showInvalidInputWindow = true;
        this.serverLoading = false;
        this.handlingSubmit = false;
        return;
      }
      this.APIparams = {
        // force recreate (remove selected times)
        site: this.selectedSite,
        unit_ID: this.selectedUnit,
        date_time: new Date(
          this.date.getTime() - this.date.getTimezoneOffset() * 60000
        ).toISOString(),
      };

      this.receivedResponse = true; // Render components

      // Fire and forget ftp status update
      this.ftpStatusMsg = "Connecting to FTP...";
      this.getFtpStatus(this.abortController.signal);

      // Data fetching can be very slow. Let's do caching stuff first.
      // Further functions will depend on cached data existing.
      this.audioStatus = null; // `missing` or `good`
      this.audioStatusMsg = "Loading audio...";

      // Check we have data in the cache
      const cacheResponse = await this.requestApiEndpoint(
        'post',
        "/cacheData",
        this.abortController.signal,
        {},
        this.APIparams
      ).catch(async (error) => {
        this.audioStatus = "missing";
        this.audioStatusMsg = "Error fetching audio data: " + error.message;
        this.serverLoading = false;
        throw Error(this.audioStatusMsg);
      });

      if (cacheResponse.data.isAudioCached && cacheResponse.data.isCsvCached) {
        if (cacheResponse.data.hasContinuousData) {
          this.audioStatus = "good";
          this.audioStatusMsg = "Continuous audio data available.";
        }
        else {
          this.audioStatus = "missing";
          this.audioStatusMsg = "No continuous data available for selected time.";
          this.serverLoading = false;
          throw Error(this.audioStatusMsg);
        }
      }
      else {
        this.audioStatus = "missing";
        this.audioStatusMsg = "Unable to fetch audio data";
        this.serverLoading = false;
        throw Error(this.audioStatusMsg);
      }

      try {
        // Only continue if audio available
        await Promise.all([
          this.fetchBackendData(this.abortController.signal),
          this.fetchWeightedSpectra(this.abortController.signal),
        ]).catch(error => {
          console.error("Error fetching data:", error);
          this.audioStatusMsg =
            "Server encountered unhandled error processing request";
        })

        // Always get ML Predictions, checkbox just controls whether it is visible or not.
        await this.fetchMlData(this.abortController.signal)

        await nextTick();
      } catch (error) {
        console.error("Error fetching analysis data:", error);
        this.audioStatusMsg =
          "Server encountered unhandled error processing request";
      } finally {
        this.serverLoading = false; // Hide loading spinner after all data is fetched
        this.handlingSubmit = false;
      }
    },
    async requestApiEndpoint(method, endpoint, controller_signal = null, params = {}, bodyData = {}, config = {}) {
      try {
        return await axiosInstance({
          method: method,
          url: endpoint,
          params: params,
          data: bodyData,
          signal: controller_signal,
          ...config
        });
      } catch (e) {
        if (e.name === "CanceledError") {
          console.log(`Api Endpoint ${endpoint} request aborted`);
        }
        console.error(`Error fetching ${endpoint}:`, e);
        throw Error(e);
      }
    },
    async postApiEndpoint(controller_signal, endpoint, requestBody, params = {}) {
      try {
        const response = await axiosInstance.post(
          endpoint,
          requestBody,
          {
            params: params,
            signal: controller_signal,
          }
        );
        return response;
      } catch (e) {
        if (e.name === "CanceledError") {
          console.log(`Api Endpoint ${endpoint} request aborted`);
          return;
        }
        console.error(`Error fetching ${endpoint}:`, e);
        throw Error(e);
      }
    },
    async fetchAvailableSites() {
      const response = await this.requestApiEndpoint('post', "/getAvailableSites");
      this.availableSites = response.data.data;
    },
    async fetchAvailableUnits() {
      const response = await this.requestApiEndpoint('post', "/getAvailableUnits", null, { site: this.selectedSite });
      this.availableUnits = response.data.data;
    },
    async getFtpStatus(controller_signal) {
      try {
        const ftpResponse = await axiosInstance.post(
          "/getFtpStatus",
          this.APIparams,
          {
            signal: controller_signal,
          }
        );

        // Debug: always log what came back if something is unexpected
        if (
          !ftpResponse ||
          !ftpResponse.data ||
          typeof ftpResponse.data !== "object" ||
          !ftpResponse.data.status
        ) {
          console.warn("Unexpected FTP status response:", ftpResponse); // includes status/code/body
          this.ftpStatus = "offline";
        } else {
          const ftpStatusStr = ftpResponse.data.status;
          const target_directory = ftpResponse.data.target_directory
            ? ftpResponse.data.target_directory
            : null;

          if (ftpStatusStr === "online" && target_directory === "available") {
            this.ftpStatus = "good";
          } else if (ftpStatusStr === "offline") {
            this.ftpStatus = "offline";
          } else if (target_directory === "missing") {
            this.ftpStatus = "unavailable";
          } else {
            // fallback / unknown combination
            console.warn("Unrecognized ftp status payload:", ftpResponse.data);
            this.ftpStatus = "offline";
          }
        }

        await this.$nextTick(); // Wait for components to render
        this.computeFtpBufferMsg();
      } catch (e) {
        if (e.name === "CanceledError") {
          console.log("getFtpStatus request aborted");
          return;
        }
        this.ftpStatus = "offline";
        console.error("Error fetching audio quality:", e);
      }
    },
    async handleReportSubmit(reportData) {
      console.log("Submitting report with data:", reportData);
      try {
        this.generateReportState = "loading";

        const response = await this.requestApiEndpoint(
          'post',
          '/reporting/generate_report',
          null,
          {},
          reportData,
          { responseType: 'blob' } // since we're expecting a pdf
        );

        const blob = new Blob([response.data], { type: "application/pdf" });

        let filename = "report.pdf";
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          // extract the filename passed by fastapi in the header
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes
          }
        }

        // Modern browers support downloading blobs via the native file dialog
        // To support legacy (and safari) we create a temporary link and click it programmatically
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = filename;
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        this.generateReportState = "ready";

        setTimeout(() => URL.revokeObjectURL(downloadLink.href), 1000);

        this.$refs.reportModal.onReportSuccess();

      } catch (error) {
        console.error("Error generating report:", error);
        this.generateReportState = "error";
        this.$refs.reportModal.onReportError('Report generation failed. Please try again.');
      }
    },
    // Find the closest timestamp (in ms) from xArray to a given 'raw' time:
    getClosestTimestamp(rawTime) {
      if (!this.xArray || this.xArray.length === 0) {
        // fallback to rawTime itself if xArray is empty
        return rawTime;
      }
      let closestNum = null;
      let minDiff = Infinity;
      for (const ts of this.xArray) {
        const tnum = new Date(ts).getTime();
        const diff = Math.abs(tnum - rawTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestNum = tnum;
        }
      }
      return closestNum;
    },
    /**
     * Find the timestamp in `sortedDates` immediately before or equal to `targetDate`.
     * @param {Date[]|string[]} sortedDates   — sorted ascending (oldest → newest)
     * @param {Date|string}     targetDate    — the cut-off date
     * @returns {Date|null}  — the floor date, or null if all entries are after target
     */
    findFloorDate(sortedDates, targetDate) {
      const t =
        targetDate instanceof Date
          ? targetDate.getTime()
          : new Date(targetDate).getTime();

      let lo = 0,
        hi = sortedDates.length - 1;
      let floorIndex = -1;

      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midVal = sortedDates[mid];
        const m =
          midVal instanceof Date
            ? midVal.getTime()
            : new Date(midVal).getTime();

        if (m <= t) {
          floorIndex = mid; // candidate – but maybe there's a later one
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      return floorIndex >= 0 ? new Date(sortedDates[floorIndex]) : null;
    },
    roundToNearest15Min(date) {
      const msPer15Min = 15 * 60 * 1000;
      return new Date(Math.floor(date.getTime() / msPer15Min) * msPer15Min);
    },
    async fetchAnalysisData(token, controller_signal) {
      try {
        const response = await axiosInstance.post("/getAnalysisData", this.APIparams, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller_signal,
        });
        const data = response.data;

        // NEW: data is now keyed by region (e.g. "2025-04-14 08:00:00 - 2025-04-14 08:15:00")
        const regionKeys = Object.keys(data);
        for (let i = 0; i < regionKeys.length; i++) {
          if (i != 0) {
            this.createNewRegion(); //first row is always initialised.
          }

          data[i].regions.forEach((item) => {
            this.selectInRegion(i, item[0], item[1]);
          });

          for (const [source, formula] of Object.entries(data[i].source_map)) {
            this.$refs.overlay.addNewSource(i, source, formula);
          }
        }

        // Get User info
        if (regionKeys.length > 0) {
          const firstKey = regionKeys[0];
          this.lastEditedBy = data[firstKey].EditedBy;
        }

        // Iterate over each region and initialize overlay rows per source.
        // regionKeys.forEach((regionKey) => {
        //   const sources = data[regionKey];
        //   for (const source in sources) {
        //     const formula = sources[source].formula;
        //     this.$refs.overlay.addNewSource(source, formula);
        //   }
        // });
      } catch (error) {
        console.error("Error fetching analysis data:", error);
      }
    },
    async fetchValidSources() {
      try {
        const response = await axiosInstance.post("/getValidSources", this.APIparams);
        this.valid_sources = response.data;
        this.selectable_sources = this.valid_sources;
      } catch (error) {
        console.error("Error fetching valid sources:", error);
      }
    },
    async fetchBackendData(controller_signal) {
      await this.fetchValidSources(); // set first
      try {
        const response = await axiosInstance.post("/soundPressureLevels", this.APIparams, {
          signal: controller_signal,
        });
        this.backendResponse = response.data;

        const x = Object.keys(this.backendResponse?.TOT_A || {});
        this.xArray = x;
        this.createNewRegion();

        await nextTick(); //backendResponse must exist for analysisData to update.
        const token = await getToken();
        if (token) {
          await this.fetchAnalysisData(token, controller_signal);
        }
        await nextTick(); //AnalysisData must update for markAreas to update.
        await this.updatePlots();
      } catch (error) {
        console.error("Error fetching backend data:", error);
      } finally {
        this.backendLoading = false;
        // this.selectable_sources = this.valid_sources.filter(
        //   (value) => !this.displayed_sources.includes(value)
        // );
        this.selectable_sources = this.valid_sources;
      }
    },
    async prepareInference() {
      const SIX_HOURS_MS = 6 * 60 * 60 * 1000;
      // Keyed by site since each site may resolve to a different inference URL.
      const siteKey = this.selectedSite || "";
      const last = this.lastPreparedAtBySite[siteKey];
      if (last && Date.now() - last < SIX_HOURS_MS) return;
      try {
        await axiosInstance.post("/prepareInference", { site: this.selectedSite || null });
        this.lastPreparedAtBySite[siteKey] = Date.now();
      } catch (e) {
        console.log("Inference server preparation failed:", e.message);
      }
    },
    async fetchMlData(controller_signal) {
      this.mlResponse = null;
      try {
        console.log("Fetching ML data...");
        this.mlStatus = "loading";
        const response = await axiosInstance.post("/get15MinPredictions", this.APIparams, {
          signal: controller_signal,
        });
        this.mlResponse = response.data;
        this.mlStatus = "finished";
        await this.updatePlots();
      } catch (error) {
        console.error("Error fetching ML data:", error);
        this.mlResponse = { predictions: {} };
        this.mlStatus = "error";
        if (error.code && error.code === "ERR_NETWORK") {
          this.mlStatus = "apiServerDown";
        }
      }
    },
    async handleExpertSubmit(formData) {
      this.showRequestExpertModal = false;
      this.requestExpertState = "loading";
      try {
        const payload = {
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          site: formData.site,
          unit: formData.unit,
          datetime: formData.datetime,
          comments: formData.comments,
        };
        const response = await axiosInstance.post("/support/request_expert_analysis", payload);
        if (response.status !== 200) {
          throw new Error("Unexpected response from server");
        }
        this.requestExpertState = "ready";
      } catch (error) {
        console.error("Error submitting expert analysis request:", error);
        this.requestExpertState = "error";
      }
    },
    getSplVisibility() {
      if (this.stage === "factsheet") {
        return "hidden";
      }
      const response = this.chartView === "Timeseries" ? "visible" : "hidden";
      return response;
    },
    getSpectraChartVisibility() {
      if (this.stage === "factsheet") {
        return "hidden";
      }
      const response = this.chartView === "Spectra" ? "visible" : "hidden";
      return response;
    },
    async createNewRegion() {
      this.currentRegion = this.regionCounter;
      this.regions[this.currentRegion] = [];
      this.regionFormulaMap[this.currentRegion] = { "regions": [], "sources_map": {} };
      this.regionCounter++;
      this.updateSelectedFeedback();
    },
    async selectInRegion(regionName, start_time, end_time) {
      this.regions[regionName] = [
        ...this.regions[regionName],
        [start_time, end_time],
      ];
      this.updateSelectedFeedback();
    },
    async deleteRegion(regionName) {
      const deletedId = parseInt(regionName, 10);

      // 1. Gather all remaining region IDs in order
      const remainingIds = Object.keys(this.regions)
        .map((k) => parseInt(k, 10))
        .filter((id) => id !== deletedId)
        .sort((a, b) => a - b);

      // 2. Rebuild regions & formula map with new zero-based keys
      const newRegions = {};
      const newFormulaMap = {};
      remainingIds.forEach((oldId, newId) => {
        newRegions[newId] = this.regions[oldId];
        if (oldId in this.regionFormulaMap) {
          newFormulaMap[newId] = this.regionFormulaMap[oldId];
        }
      });

      // 3. Replace your state
      this.regions = newRegions;
      this.regionFormulaMap = newFormulaMap;
      this.regionCounter = remainingIds.length;

      // 4. Adjust currentRegion pointer
      if (this.currentRegion === deletedId) {
        // If they deleted the active region, clamp to last region
        this.currentRegion = Math.max(0, this.regionCounter - 1);
      } else if (this.currentRegion > deletedId) {
        // If their index was above the deleted one, shift it down
        this.currentRegion--;
      }

      // 5. Trigger your re-render/update hooks
      this.updateSelectedFeedback();
      this.updateSpectraChartOptions();
    },
    async deleteSource(regionName, sourceName) {
      delete this.regionFormulaMap[regionName][sourceName];
    },
    async selectRegion(region) {
      this.currentRegion = parseInt(region);
      this.updateSelectedFeedback();
    },
    async unselectAllInRegion() {
      this.regions[this.currentRegion] = [];
      this.updateSelectedFeedback();
    },
    async selectAllInRegion() {
      const dt = new Date(this.APIparams.date_time.split("Z")[0]);
      const end_dt = new Date(dt.getTime() + 15 * 60 * 1000);
      this.regions[this.currentRegion] = [
        [this.formatDateTime(dt), this.formatDateTime(end_dt)],
      ];
      this.updateSelectedFeedback();
    },
    async updatePlots() {
      const plotData = [];
      if (!this.backendResponse || !this.APIparams.date_time) return;

      this.audioTime = new Date(
        this.APIparams.date_time.split("Z")[0]
      ).getTime();

      // Bankers round TOT A but, keep do not mutate TOT_A for reporting calcs.
      const tot_a_y = Object.values(this.backendResponse.TOT_A).map(v => bankersRound(v));
      plotData.push({
        name: "TOT A",
        x: this.xArray,
        y: tot_a_y,
        line: { dash: "solid", width: 3 },
      });

      this.filteredTrace = this.getFilteredLevelsTrace();
      plotData.push(this.filteredTrace);

      const series = plotData.map((trace) => {
        const color = this.colorForSeries(trace.name);
        const data = trace.x.map((xi, idx) => [xi, trace.y[idx]]);
        const lineType = trace.line.dash === "dot" ? "dotted" : "solid";
        const isCircleLine = ["TOT A", FILTERED_LEVELS_NAME].includes(trace.name);
        return {
          name: trace.name,
          type: "line",
          data: data,
          smooth: false,
          lineStyle: {
            type: lineType,
            width: trace.line.width,
            color: color,
          },
          itemStyle: { color: color },
          showSymbol: isCircleLine, // <-- circles on
          symbol: isCircleLine ? "circle" : "none",
          symbolSize: isCircleLine ? 5 : 0,
          z: isCircleLine ? 3 : 2,
        };
      });
      const minTime = new Date(this.xArray[0]).getTime();
      // const maxTime = this.xArray[this.xArray.length - 1].getTime();
      const maxTime = minTime + 15 * 60 * 1000; // assume 15 minutes of audio

      // use seperate series for markLine and use first index
      plotData.unshift({
        data: [],
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: {
            color: "black",
            width: 2,
            type: "dashed",
          },
          data: [
            {
              xAxis: [],
            },
          ],
          label: {
            show: false,
            // position: "end",
            // formatter: (params) => this.formatMarkLine(params.value),
          },
        },
      });

      this.sharedZoom = {
        minTime: minTime,
        maxTime: maxTime,
        minIndex: 0,
        maxIndex: 100,
      };

      // Capture class names for legend + color mapping (even though we don’t draw lines)
      this.predictionSeriesNames = [
        ...Object.keys(this.backendResponse?.predictions || {}),
        ...Object.keys(this.mlResponse?.predictions || {}),
      ];
      this.predictionSeriesNames.forEach((name) => {
        if (!(name in this.seriesVisibility)) {
          this.seriesVisibility[name] = true;
        }
      });

      // Legend will include the top 5 ML classes by average dB, plus TOT A, Filtered, and Unaccounted (if on)
      const predsData = {
        ...(this.backendResponse?.predictions || {}),
        ...(this.mlResponse?.predictions || {}),
      };
      const avgDb = (name) => {
        const values = Object.values(predsData[name] || {}).filter(
          (v) => typeof v === "number" && isFinite(v)
        );
        return values.length
          ? values.reduce((sum, v) => sum + v, 0) / values.length
          : -Infinity;
      };
      const topPredictionNames = this.predictionSeriesNames
        .slice()
        .sort((a, b) => avgDb(b) - avgDb(a))
        .slice(0, 5);

      const legendData = [
        "TOT A",
        FILTERED_LEVELS_NAME,
        ...topPredictionNames,
        ...(this.includeUnaccounted ? ["Unaccounted for Noise"] : []),
      ];
      const selectedMapping = {
        "TOT A": true,
        [FILTERED_LEVELS_NAME]: true,
        ...topPredictionNames.reduce((acc, n) => {
          acc[n] = this.seriesVisibility[n] ?? true;
          return acc;
        }, {}),
        ...(this.includeUnaccounted ? { "Unaccounted for Noise": true } : {}),
      };
      this.timeseriesChartOptions = {
        tooltip: {
          trigger: "axis",
          // Anchor tooltip top left corner to cursor
          position: (point, params, dom, rect, size) => {
            const [x, y] = point;
            return [x, y];
          },
          formatter: (params) => {
            const title = params[0].axisValueLabel;

            const mapped = params
              .filter((item) => !item.seriesName.startsWith("__"))  // Region selections start with "__"
              .map((item) => {
                // check if the value is a number since gaps in the series are null
                const yValue = typeof item.value[1] === "number" ? item.value[1] : null;
                return { item, yValue };
              })
              .filter(({ yValue }) => yValue !== null);

            const pinned = mapped.filter(({ item }) => this.pinnedSeriesNames.has(item.seriesName));
            const top5 = mapped
              .filter(({ item }) => !this.pinnedSeriesNames.has(item.seriesName))
              .sort((a, b) => b.yValue - a.yValue)
              .slice(0, 5);

            let content = `<b>${title}</b><br/>`;
            [...pinned, ...top5].forEach(({ item, yValue }) => {
              content += `<span style="display:inline-block;width:12px;height:12px;background-color:${item.color};border-radius:50%;margin-right:5px;"></span>
                          ${item.seriesName}: <b style="float:right;">${yValue.toFixed(0)}</b><br/>`;
            });
            return content;
          },
        },
        legend: {
          data: legendData,
          // Formatted appends the frequency range selected in legend
          formatter: (name) => {
            if (name === FILTERED_LEVELS_NAME) {
              return `${FILTERED_LEVELS_NAME} (${this.range[0]}Hz-${this.range[1]}Hz)`;
            }
            return name;
          },
          top: 5,
          left: "center",
          selected: selectedMapping,
        },
        xAxis: {
          type: "time",
          boundaryGap: false,
          min: this.sharedZoom.minTime,
          max: this.sharedZoom.maxTime,
        },
        yAxis: {
          type: "value",
          min: 20, // hardcoded 20 dB minimum value
          name: "dB(A)",
          nameLocation: "middle",
          nameGap: 25,
        },
        grid: {
          left: 50,
          top: 25,
          right: 50,
          bottom: 20,
        },
        backgroundColor: "#eee",
        dataZoom: [
          {
            type: "slider",
            xAxisIndex: 0,
            bottom: 20,
          },
        ],
        series: series,
      };

      this.lineSeries = series;

      this.updateSelectedFeedback();
      this.updateSpectraChartOptions();
      await nextTick();
      this.updateMlContribOptions();
    },
    selectAllAiPredictions() {
      this.predictionSeriesNames.forEach((name) => {
        this.seriesVisibility[name] = true;
      });
      this.includeUnaccounted = true;
      this.updateLegendSelection();
    },

    deselectAllAiPredictions() {
      this.predictionSeriesNames.forEach((name) => {
        this.seriesVisibility[name] = false;
      });
      this.includeUnaccounted = false;
      this.updateLegendSelection();
    },
    updateLegendSelection() {
      const prevLegend = this.timeseriesChartOptions.legend || {};
      const selected = { ...(prevLegend.selected || {}) };
      this.predictionSeriesNames.forEach(
        (n) => (selected[n] = !!this.seriesVisibility[n])
      );
      selected["Unaccounted for Noise"] = !!this.includeUnaccounted;
      this.timeseriesChartOptions = {
        ...this.timeseriesChartOptions,
        legend: { ...prevLegend, selected },
      };
    },
    updateSpectraChartOptions() {
      // Make sure we're handling the Spectra view.
      if (this.chartView !== "Spectra") return;

      // Hide the time series chart (assuming you have an options object for it).
      if (this.timeseriesChartOptions) {
        this.timeseriesChartOptions.visible = "hidden";
      }

      // Filter and sort the spectra data:
      // Only include bands that exist in the labeledSeries array.
      const filteredData = Object.entries(this.backendResponse.third_octaves)
        // .filter(([band]) => this.labeledSeries.includes(band))
        .sort((a, b) => a[0].localeCompare(b[0])); // sort alphabetically by band key

      // Split the filtered data into separate arrays for the x-axis (bands) and y-axis (values).
      const categories = filteredData.map(([band]) => band);
      const dataValues = Object.keys(filteredData).map((key) => {
        const value = filteredData[key];
        return value[0];
      });

      // Build the spectra bar chart options.
      // (Assuming you're using a charting library that takes a configuration object with properties like 'chart', 'series', and 'xaxis'.)
      this.spectraChartOptions = {
        xAxis: {
          type: "category",
          categories: categories,
          name: "Octave Bands",
        },
        yAxis: {
          type: "value",
          name: "dB",
          // Sets minimum Y axis value, causes strange clipping
          // Comment out while waiting response on preferred outcome from Clayton
          //min: 0,
        },
        series: [
          {
            type: "bar",
            data: dataValues,
            itemStyle: {
              color: "#009B69",
              opacity: 0.6,
            },
            name: "LZeq",
          },
        ],
      };
    },
    getChartGridBounds() {
      const chartDom = this.$refs.splChartRef.getDom();
      if (!chartDom || !this.timeseriesChartOptions.grid) return null;
      const grid = this.timeseriesChartOptions.grid;
      const dzHeight = 30;
      const left =
        typeof grid.left === "number" ? grid.left : parseInt(grid.left, 10);
      const top =
        typeof grid.top === "number" ? grid.top : parseInt(grid.top, 10);
      const right =
        chartDom.offsetWidth -
        (typeof grid.right === "number"
          ? grid.right
          : parseInt(grid.right, 10));
      const bottom =
        chartDom.offsetHeight -
        (typeof grid.bottom === "number"
          ? grid.bottom + dzHeight
          : parseInt(grid.bottom, 10) + dzHeight);
      return { left, top, right, bottom };
    },
    async fetchWeightedSpectra(controller_signal) {
      try {
        const response = await axiosInstance.post(
          "/weightedSpectra",
          this.APIparams,
          {
            signal: controller_signal,
          }
        );
        // Assume response.data.spectra is an object with keys "A", "C", "Z"
        this.weightedSpectra = response.data.spectra;
      } catch (error) {
        console.error("Error fetching weighted spectra:", error);
      }
    },
    async handleDataZoomEvent(e) {
      this.isZooming = true;
      this.dataZoomEvent = e;

      if (!e || this.isDragging) {
        return;
      }
    },
    handleDataZoom() {
      if (!this.dataZoomEvent) {
        return;
      }
      const e = this.dataZoomEvent;
      const newMinIndex = e.start;
      const newMaxIndex = e.end;

      // Convert category index to an absolute timestamp.
      const baseTime = new Date(
        this.APIparams.date_time.split("Z")[0]
      ).getTime();
      const totalDuration = 15 * 60 * 1000;
      const newMinTime = baseTime + Math.round((totalDuration * e.start) / 100);
      const newMaxTime = baseTime + Math.round((totalDuration * e.end) / 100);

      this.updateSharedZoom({
        minIndex: newMinIndex,
        maxIndex: newMaxIndex,
        minTime: newMinTime,
        maxTime: newMaxTime,
      });
    },
    async updateSharedZoom(newZoom) {
      if (
        newZoom.minTime != this.sharedZoom.minTime ||
        newZoom.maxTime != this.sharedZoom.maxTime
      ) {
        this.sharedZoom = newZoom;
      }
    },
    handleLegendSelectChanged(evt) {
      // optional: keep a local copy for “Show/Hide all” buttons
      const next = { ...this.seriesVisibility };
      (this.predictionSeriesNames || []).forEach((name) => {
        if (
          evt.selected &&
          Object.prototype.hasOwnProperty.call(evt.selected, name)
        ) {
          next[name] = !!evt.selected[name];
        }
      });
      this.seriesVisibility = next;

      // write legend selected state back into options
      const prevLegend = this.timeseriesChartOptions.legend || {};
      this.timeseriesChartOptions = {
        ...this.timeseriesChartOptions,
        legend: { ...prevLegend, selected: { ...evt.selected } },
      };
    },
    formatDateTime(dt) {
      const year = dt.getFullYear();
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const day = String(dt.getDate()).padStart(2, "0");
      const hours = String(dt.getHours()).padStart(2, "0");
      const minutes = String(dt.getMinutes()).padStart(2, "0");
      const seconds = String(dt.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    interactiveDatetimeFormatter(dt) {
      const hours = String(dt.getHours()).padStart(2, "0");
      const minutes = String(dt.getMinutes()).padStart(2, "0");
      const seconds = String(dt.getSeconds()).padStart(2, "0");
      const next_dt = new Date(dt);
      next_dt.setSeconds(dt.getSeconds() + 10);
      const next_hours = String(next_dt.getHours()).padStart(2, "0");
      const next_minutes = String(next_dt.getMinutes()).padStart(2, "0");
      const next_seconds = String(next_dt.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds} to ${next_hours}:${next_minutes}:${next_seconds}`;
    },
    formatMarkLine(value) {
      if (!value) return "";
      const date = new Date(value);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    formatLegend(key) {
      const seriesItem = this.timeseriesChartOptions.series.find(
        (item) => item.name === key
      );
      return seriesItem ? seriesItem.lineStyle?.color || null : "black";
    },
    parseToHz(str) {
      const m = str.match(
        /^\s*([+-]?\d*\.?\d+(?:[eE][+-]?\d+)?)\s*(?:([kK]?)[Hh][Zz])?\s*$/
      );
      if (!m) return NaN;
      let value = parseFloat(m[1]);
      if (m[2] && m[2].toLowerCase() === "k") {
        value *= 1000;
      }
      return value;
    },
    getMlValue(source) {
      if (!this.mlResponse || !this.mlResponse.predictions[source]) return;
      const refTime = this.selectedTimestamps[0];
      return this.mlResponse.predictions[source][refTime];
    },
    roundToNearest10(timestamp) {
      const d = new Date(timestamp);
      const remainder = d.getSeconds() % 10;
      const padding = remainder >= 5 ? 10 - remainder : -remainder;
      d.setSeconds(d.getSeconds() + padding);
      d.setMilliseconds(0);
      return d;
    },
    async addNewSource(regionName, source) {
      if (!this.regionFormulaMap[this.currentRegion]) {
        this.regionFormulaMap[this.currentRegion] = {};
      }
      this.regionFormulaMap[regionName][source] = null; // initialize with null formula
    },

    // --- Helper Function: Convert interval [startStr, endStr] to numeric values ---
    convertIntervalToNumbers(interval) {
      const [startStr, endStr] = interval;
      return [new Date(startStr).getTime(), new Date(endStr).getTime()];
    },

    // --- Helper Function: Convert numeric interval back to formatted strings ---
    convertIntervalToStrings(interval) {
      const [startNum, endNum] = interval;
      return [
        this.formatDateTime(new Date(startNum)),
        this.formatDateTime(new Date(endNum)),
      ];
    },

    // --- Helper Function: Union Intervals ---
    unionIntervals(existingIntervals, newInterval) {
      // existingIntervals is an array of intervals stored as [startStr, endStr].
      // newInterval is in numeric format: [startTime, endTime].
      const intervals = existingIntervals.map((iv) =>
        this.convertIntervalToNumbers(iv)
      );
      // Include the new interval.
      intervals.push(newInterval);

      // Sort intervals by the start time.
      intervals.sort((a, b) => a[0] - b[0]);

      // Merge overlapping or adjacent intervals.
      const merged = [];
      for (const interval of intervals) {
        if (!merged.length) {
          merged.push(interval);
        } else {
          const last = merged[merged.length - 1];
          if (interval[0] <= last[1]) {
            // Overlapping or touching; merge them.
            last[1] = Math.max(last[1], interval[1]);
          } else {
            merged.push(interval);
          }
        }
      }
      // Convert back to the string representation.
      return merged.map((iv) => this.convertIntervalToStrings(iv));
    },

    // --- Helper Function: Difference Intervals ---
    differenceIntervals(existingIntervals, removalInterval) {
      // Subtract removalInterval ([rStart, rEnd] in ms) from each existing interval.
      const result = [];
      for (const interval of existingIntervals) {
        const [start, end] = this.convertIntervalToNumbers(interval);
        const [rStart, rEnd] = removalInterval;
        // No overlap? Keep the interval.
        if (rEnd <= start || rStart >= end) {
          result.push([start, end]);
        } else {
          // There is an overlap.
          if (rStart > start) {
            result.push([start, rStart]);
          }
          if (rEnd < end) {
            result.push([rEnd, end]);
          }
        }
      }
      // Sort the resulting intervals.
      result.sort((a, b) => a[0] - b[0]);
      // Convert numeric intervals back to formatted strings.
      return result.map((iv) => this.convertIntervalToStrings(iv));
    },

    applyRegionAtTime(time, mode) {
      const start = time;
      // Try to find this exact start in xArray; if found, use the very next xArray entry as “end”:
      let end = start + 10000;
      const idx = this.xArray.findIndex(
        (ts) => new Date(ts).getTime() === start
      );
      if (idx !== -1 && idx + 1 < this.xArray.length) {
        end = new Date(this.xArray[idx + 1]).getTime();
      }
      const dragInterval = [start, end];

      if (mode === "selecting") {
        // first strip overlaps from *other* regions
        this.enforceNoOverlap(this.currentRegion);
        // now safely union into the current region
        this.regions[this.currentRegion] = this.unionIntervals(
          this.regions[this.currentRegion] || [],
          dragInterval
        );
      } else {
        this.regions[this.currentRegion] = this.differenceIntervals(
          this.regions[this.currentRegion] || [],
          dragInterval
        );
      }
      this.updateSelectedFeedback();
    },
    mergeRegionRanges(regionArray) {
      const sorted = regionArray
        .map(([s, e]) => [new Date(s).getTime(), new Date(e).getTime()])
        .sort((a, b) => a[0] - b[0]);

      const merged = [];
      for (const [start, end] of sorted) {
        if (!merged.length || merged[merged.length - 1][1] < start) {
          merged.push([start, end]);
        } else {
          merged[merged.length - 1][1] = Math.max(
            merged[merged.length - 1][1],
            end
          );
        }
      }

      return merged.map(([s, e]) => [
        this.formatDateTime(new Date(s)),
        this.formatDateTime(new Date(e)),
      ]);
    },
    // Helper: Apply selection/unselection over a range of time values.
    applyRange(start, end, mode) {
      const step = 10 * 1000; // step of 10 seconds (in ms)
      for (let t = start; t <= end; t += step) {
        this.applyRegionAtTime(t, mode);
      }
    },

    handleChartMouseDown(event) {
      // Check if the mouse event is within the chart grid bounds.
      const bounds = this.getChartGridBounds();
      if (
        !bounds ||
        event.offsetX < bounds.left ||
        event.offsetX > bounds.right ||
        event.offsetY < bounds.top ||
        event.offsetY > bounds.bottom
      ) {
        // Outside chart area: let the parent zooming logic handle this.
        this.isZooming = true;
        return;
      }

      // Update feedback on where the mouse was pressed.
      this.mousedownFeedback = {
        visible: true,
        x: event.offsetX,
        y: event.offsetY,
      };

      // Convert pixel location to a data point (here, a time value).
      const dataPoint = this.$refs.splChartRef.convertFromPixel(
        { seriesIndex: 0 },
        [event.offsetX, event.offsetY]
      );
      if (!dataPoint) return;
      // Instead of roundToNearest10(), snap to the closest backend‐provided stamp:
      const raw = dataPoint[0];
      const clickedTimeEpoch = this.getClosestTimestamp(raw);

      // Record the current selection state (ensure a deep copy)
      this.initialRegionSnapshot = JSON.parse(
        JSON.stringify(this.regions[this.currentRegion] || [])
      );

      // Check if the click occurred in an existing time range for the current region.
      this.currentDragRegionRange = null;
      if (Object.keys(this.regions).length == 0) this.createNewRegion();
      const region = this.regions[this.currentRegion] || [];
      this.dragMode = "selecting"; //initialise
      this.extendEdge = null;
      for (const [startTimeStr, endTimeStr] of region) {
        const startTime = new Date(startTimeStr).getTime();
        const endTime = new Date(endTimeStr).getTime();
        if (clickedTimeEpoch >= startTime && clickedTimeEpoch <= endTime) {
          // The click is inside an existing range.
          this.currentDragRegionRange = [startTime, endTime];
          // update edge state
          if (clickedTimeEpoch === startTime) {
            this.dragMode = "selecting"; // We want to extend the interval leftwards.
            this.extendEdge = "left";
          } else if (clickedTimeEpoch === endTime) {
            this.dragMode = "selecting"; // Extend to the right.
            this.extendEdge = "right";
          } else {
            // Otherwise, if clicked somewhere in the middle, default to unselecting.
            this.dragMode = "unselecting";
            this.extendEdge = null;
          }
          break;
        }
      }

      // -- Shift+click logic --
      // Use the last selected timestamp to determine the new range.
      if (event.shiftKey && this.lastSelectedTimestamp != null) {
        const previousTime = this.lastSelectedTimestamp;
        const startRange = Math.min(previousTime, clickedTimeEpoch);
        const endRange = Math.max(previousTime, clickedTimeEpoch);

        // If the midpoint is already within a region then we'll assume the user wants to unselect;
        // otherwise, we will select.
        const tMid = Math.floor((startRange + endRange) / 2);
        const midInRegion = region.some(([start, end]) => {
          const startMs = new Date(start).getTime();
          const endMs = new Date(end).getTime();
          return tMid >= startMs && tMid < endMs;
        });
        this.dragMode = midInRegion ? "unselecting" : "selecting";

        // Apply the determined mode over the entire range.
        this.applyRange(startRange, endRange, this.dragMode);
        this.lastSelectedTimestamp = clickedTimeEpoch;
        this.updateSelectedFeedback();

        // For shift+click, we do not continue with a drag.
        return;
      }

      // -- Normal click / drag logic --
      // Store the start time for dragging.
      this.dragStartTimeValue = clickedTimeEpoch;
      this.isDragging = true;

      // Decide the default mode: if the click is in an existing range, default to unselecting.
      if (this.currentDragRegionRange) {
        this.dragMode = "unselecting";
      } else {
        this.dragMode = "selecting";
        // For a simple click, apply the selection immediately.
        this.applyRegionAtTime(clickedTimeEpoch, "selecting");
      }
      this.lastSelectedTimestamp = clickedTimeEpoch;
      this.updateSelectedFeedback();
    },

    handleChartMouseMove(event) {
      // Only process if in dragging mode.
      if (!this.isDragging || this.dragStartTimeValue === null) return;
      const dataPoint = this.$refs.splChartRef.convertFromPixel(
        { seriesIndex: 0 },
        [event.offsetX, event.offsetY]
      );
      if (!dataPoint) return;
      // const currentTime = this.roundToNearest10(dataPoint[0]);
      // const currentTimeEpoch = currentTime.getTime();
      // Snap to the closest xArray stamp:
      const raw = dataPoint[0];
      const currentTimeEpoch = this.getClosestTimestamp(raw);

      // Build the drag interval (in numeric ms)
      let newIntervals;

      // If we’re extending from an edge of an interval...
      if (this.extendEdge === "left" && this.currentDragRegionRange) {
        const [regionStart, regionEnd] = this.currentDragRegionRange;
        // Only extend if dragging left (currentTime is less than the region’s start)
        if (currentTimeEpoch < regionStart) {
          const updatedInterval = [currentTimeEpoch, regionEnd];
          const newSnapshot = this.initialRegionSnapshot.filter((iv) => {
            const [s, e] = this.convertIntervalToNumbers(iv);
            return Math.abs(s - regionStart) > 1 && Math.abs(e - regionEnd) > 1;
          });
          newIntervals = this.unionIntervals(newSnapshot, updatedInterval);
        } else if (currentTimeEpoch >= regionEnd) {
          newIntervals = this.differenceIntervals(this.initialRegionSnapshot, [
            regionStart,
            regionEnd,
          ]);
        } else if (
          currentTimeEpoch > regionStart &&
          currentTimeEpoch < regionEnd
        ) {
          newIntervals = this.differenceIntervals(this.initialRegionSnapshot, [
            regionStart,
            currentTimeEpoch,
          ]);
        } else {
          // If dragging toward the right, we do nothing and revert.
          newIntervals = this.initialRegionSnapshot;
        }
      } else if (this.extendEdge === "right" && this.currentDragRegionRange) {
        const [regionStart, regionEnd] = this.currentDragRegionRange;
        // Extend only if dragging right (currentTime greater than regionEnd).
        if (currentTimeEpoch > regionEnd) {
          const updatedInterval = [regionStart, currentTimeEpoch];
          const newSnapshot = this.initialRegionSnapshot.filter((iv) => {
            const [s, e] = this.convertIntervalToNumbers(iv);
            return Math.abs(s - regionStart) > 1 && Math.abs(e - regionEnd) > 1;
          });
          // newSnapshot.push(updatedInterval);
          newIntervals = this.unionIntervals(newSnapshot, updatedInterval);
        } else if (currentTimeEpoch <= regionStart) {
          newIntervals = this.differenceIntervals(this.initialRegionSnapshot, [
            regionStart,
            regionEnd,
          ]);
        } else if (
          currentTimeEpoch < regionEnd &&
          currentTimeEpoch > regionStart
        ) {
          newIntervals = this.differenceIntervals(this.initialRegionSnapshot, [
            currentTimeEpoch,
            regionEnd,
          ]);
        } else {
          newIntervals = this.initialRegionSnapshot;
        }
      } else {
        // Otherwise, for normal selection/unselection drags:
        let dragInterval;
        if (currentTimeEpoch < this.dragStartTimeValue) {
          // If dragging left, anchor the minimum 10 seconds to the left of the start time.
          dragInterval = [currentTimeEpoch, this.dragStartTimeValue];
        } else {
          // If dragging right, use the 10-second minimum to the right.
          dragInterval = [this.dragStartTimeValue, currentTimeEpoch + 10000];
        }
        if (this.dragMode === "selecting") {
          newIntervals = this.unionIntervals(
            this.initialRegionSnapshot,
            dragInterval
          );
        } else {
          newIntervals = this.differenceIntervals(
            this.initialRegionSnapshot,
            dragInterval
          );
        }
      }
      this.regions[this.currentRegion] = newIntervals;
      this.updateSelectedFeedback();
    },

    handleChartMouseUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.dragStartTimeValue = null;
      this.mousedownFeedback.visible = false;
      this.currentDragRegionRange = null;
      this.initialRegionSnapshot = [];
      this.enforceNoOverlap(this.currentRegion);
      this.updateSelectedFeedback();
    },

    pageMouseUpHanlder() {
      this.isZooming = false;
    },

    enforceNoOverlap(regionName) {
      const keep = this.regions[regionName] || [];
      // For each interval in the region we just edited...
      keep.forEach(([startStr, endStr]) => {
        const start = new Date(startStr).getTime();
        const end = new Date(endStr).getTime();
        // Strip that slice out of *every* other region
        Object.entries(this.regions).forEach(([r, list]) => {
          if (r !== String(regionName)) {
            this.regions[r] = this.differenceIntervals(list, [start, end]);
          }
        });
      });
    },

    getFilteredLevelsTrace() {
      if (!this.backendResponse.third_octaves) return null;
      const combinedPower = {};
      Object.keys(this.backendResponse.third_octaves).forEach((band) => {
        const freq = this.parseToHz(band);
        if (freq >= this.range[0] && freq <= this.range[1]) {
          const bandData = this.backendResponse.third_octaves[band];
          Object.keys(bandData).forEach((timestamp) => {
            const dBValue = bandData[timestamp];
            const linearPower = Math.pow(10, dBValue / 10);
            combinedPower[timestamp] =
              (combinedPower[timestamp] || 0) + linearPower;
          });
        }
      });
      const timestamps = Object.keys(combinedPower).sort(
        (a, b) => new Date(a) - new Date(b)
      );
      const combineddB = timestamps.map((ts) => {
        const sumLinear = combinedPower[ts];
        return sumLinear > 0 ? 10 * Math.log10(sumLinear) : -Infinity;
      });
      return {
        name: FILTERED_LEVELS_NAME,
        x: timestamps,
        y: combineddB,
        line: { dash: "dot", width: 2 },
      };
    },
    saveRow(row) {
      this.saveDeterminedValue(
        row.predictionKey,
        this.selectedTimestamps[0],
        row.determinedValue
      );
      this.updateDeterminedValue(
        row.source,
        row.predictionKey,
        this.selectedTimestamps[0],
        row.determinedValue
      );
    },
    updateDeterminedValue(source, predictionKey, xValue, newValue) {
      const response = this.mlResponse;
      if (!response.determined) response.determined = {};
      if (!response.determined[predictionKey])
        response.determined[predictionKey] = {};
      response.determined[predictionKey][xValue] = newValue;
      this.mlResponse = { ...response };
      const seriesItem = this.timeseriesChartOptions.series.find(
        (item) => item.name === predictionKey
      );
      if (seriesItem) {
        const dataPoint = seriesItem.data.find((point) => point[0] === xValue);
        if (dataPoint) {
          dataPoint[1] = newValue;
        } else {
          seriesItem.data.push([xValue, newValue]);
        }
      } else {
        this.timeseriesChartOptions.series.push({
          name: predictionKey,
          type: "line",
          data: [[xValue, newValue]],
          smooth: false,
          lineStyle: { type: "dotted" },
        });
      }
      this.timeseriesChartOptions = { ...this.timeseriesChartOptions };
    },
    async startup() {
      this.availableUnits = [{ unit: "Please select a site first", is_enabled: false }];
      await this.fetchAvailableSites();
      this.finishedMount = true;
    },
    updateRegionSeriesMarkAreas() {
      if (!this.timeseriesChartOptions?.series) return;

      // Keep only non-region series (base lines + ML areas).
      const base = (this.timeseriesChartOptions.series || []).filter(
        (s) => !s._isRegion
      );

      if (Object.keys(this.regions).length == 0) {
        this.createNewRegion();
      }

      let colorIndex = 0;
      let seriesId = 0;
      const regionSeries = [];

      Object.entries(this.regions).forEach(([regionId]) => {
        seriesId =
          Number(Object.keys(this.lineSeries).length) + Number(regionId);

        const baseColor =
          this.regionColours[colorIndex] || "rgba(100, 100, 100, 0.2)";
        colorIndex++;

        const isCurrent = parseInt(regionId) === this.currentRegion;

        regionSeries.push({
          name: `__region_${regionId}`,
          type: "line",
          data: [],
          lineStyle: { width: 0 },
          showSymbol: false,
          silent: true,
          _isRegion: true, // <— sentinel to avoid collisions later
          markArea: {
            silent: true,
            itemStyle: {
              color: this.setAlpha(baseColor, isCurrent ? 0.5 : 0.2),
              borderWidth: isCurrent ? 2 : 0,
              borderColor: this.setAlpha(baseColor, 1),
            },
            data: (this.regions[regionId] || []).map(([startStr, endStr]) => [
              { xAxis: new Date(startStr).getTime() },
              { xAxis: new Date(endStr).getTime() },
            ]),
          },
        });

        this.timeseriesChartOptions.series[seriesId] = {
          ...this.timeseriesChartOptions.series[seriesId],
        }; // force reactivity
      });

      this.timeseriesChartOptions.series = [...base, ...regionSeries];

      // this.updateLegendSelection();
    },
    setAlpha(rgbaString, alpha) {
      const match = rgbaString.match(
        /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/
      );
      if (!match) return rgbaString;
      const [, r, g, b] = match;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    updateSelectedFeedback() {
      if (
        !this.$refs.splChartRef ||
        !this.timeseriesChartOptions ||
        !this.timeseriesChartOptions.series ||
        !this.timeseriesChartOptions.series[0]
      )
        return;
      this.updateRegionSeriesMarkAreas();
    },
    setStage(stage) {
      this.stage = stage;
      if (stage === "analysis") {
        return;
      }
      this.updateAnalysisData();
    },
    getSelectedSourcesFromAnalysis() {
      // Extract all sources that have formulas defined across all regions
      const selectedSources = new Set();

      Object.values(this.regionFormulaMap || {}).forEach((regionData) => {
        if (regionData.sources_map) {
          Object.keys(regionData.sources_map).forEach((sourceName) => {
            if (regionData.sources_map[sourceName]) { // Only include sources with formulas
              selectedSources.add(sourceName);
            }
          });
        }
      });

      // Return as array, fallback to [] if no sources selected
      const result = Array.from(selectedSources);
      return result.length > 0 ? result : [];
    },
    handleFormulaChange(sourcesMap) {
      //console.log("Regions keys: ", this.regions);
      //console.log("Received sourcesMap: ", sourcesMap);
      if (!sourcesMap) return;

      // Make sourcesMap available to SoundPressureLevels component to send to report modal
      this.sourcesMap = sourcesMap;

      const allRegions = Object.keys(this.regions);

      const map = {};

      allRegions.forEach((region) => {
        // Get the regions intervals from 'regions' object; if not defined, default to empty array.
        const regionIntervals = this.regions[region] || [];
        // Get the array of sources for this region; if not defined, default to an empty array.
        const sourcesArray = sourcesMap[region] || [];

        // Build an object mapping each source's name to its formula.
        const sourcesObj = {};

        sourcesArray.forEach((source) => {
          sourcesObj[source.name] = source.formula;
        });

        // Assign the constructed regions and sources object for the given region.
        map[region] = {
          regions: regionIntervals,
          sources_map: sourcesObj,
        };
      });

      // Save the computed map in the Vue instance.
      this.regionFormulaMap = map;
    },
    async updateAnalysisData() {
      // Take immutable snapshots BEFORE any await
      const regionMapSnapshot = JSON.parse(
        JSON.stringify(this.regionFormulaMap)
      );
      const paramsSnapshot = {
        unit_ID: this.APIparams.unit_ID,
        Site: this.APIparams.site,
        date_time_start: this.APIparams.date_time,
      };

      // Create sources_map by extracting the 'formula' value per source.
      const sources_map = {};
      for (const source in regionMapSnapshot) {
        sources_map[source] = regionMapSnapshot[source].formula || "";
      }

      const payload = {
        ...paramsSnapshot,
        region_map: this.regionFormulaMap || {},
      };
      try {
        await axiosInstance.post("/updateAnalysis", payload);
      } catch (error) {
        console.error("Error uploading analysis:", error);
      }
    },
    confirmUnsavedYes() {
      this.unsavedModalResolve({ save: true, cancel: false });
    },
    confirmUnsavedNo() {
      this.unsavedModalResolve({ save: false, cancel: false });
    },
    confirmUnsavedCancel() {
      this.unsavedModalResolve({ save: false, cancel: true });
    },
    async handleUnmount() {
      if (
        !this.regions ||
        (this.regions !== null &&
          typeof this.regions === "object" &&
          Object.values(this.regions).every(
            (val) => Array.isArray(val) && val.length === 0
          ))
      )
        return false;
      const { save, cancel } = await new Promise((resolve) => {
        this.unsavedModalResolve = resolve;
        this.showUnsavedModal = true;
      });
      this.showUnsavedModal = false;
      if (cancel) return true;
      if (save) {
        await this.updateAnalysisData();
      }
      return false;
    },
    // --- Convert per-timestamp class LAeqs to normalized linear contributions (%), plus dB overlays ---
    computeMlContribOverTime() {
      if (
        !this.mlResponse ||
        !this.mlResponse.predictions ||
        !this.backendResponse ||
        !this.backendResponse.TOT_A
      ) {
        return {
          classes: [],
          pctDisplayByClass: {},
          pctRawByClass: {},
          unaccountedPctDisplay: [],
          totALine: [],
          laeqByClass: {},
          diagInfo: [],
          fitScaleArr: [],
        };
      }

      const preds = this.mlResponse.predictions;
      const totA = this.backendResponse.TOT_A;
      const timestamps = this.xArray.slice();
      const classes = Object.keys(preds || {});

      const pctRawByClass = {};
      const pctDisplayByClass = {};
      const laeqByClass = {};
      classes.forEach((c) => {
        pctRawByClass[c] = [];
        pctDisplayByClass[c] = [];
        laeqByClass[c] = [];
      });

      const unaccountedPctDisplay = [];
      const totALine = [];
      const diagInfo = [];
      const fitScaleArr = [];

      for (const ts of timestamps) {
        const tot_dB = totA?.[ts];
        const E_total =
          typeof tot_dB === "number" ? Math.pow(10, tot_dB / 10) : 0;

        const Ei = {};
        classes.forEach((c) => {
          const dB = preds[c]?.[ts];
          const e =
            typeof dB === "number" && isFinite(dB) ? Math.pow(10, dB / 10) : 0;
          Ei[c] = e;
          laeqByClass[c].push([ts, typeof dB === "number" ? bankersRound(dB) : null]);
        });

        let sumRawPct = 0;
        classes.forEach((c) => {
          const rawPct = E_total > 0 ? (100 * Ei[c]) / E_total : 0;
          pctRawByClass[c].push([ts, rawPct]);
          sumRawPct += rawPct;
        });

        const underPct = Math.max(100 - sumRawPct, 0);
        const overPct = Math.max(sumRawPct - 100, 0);

        // per-timestamp fit scale (used for overlay normalisation)
        let fitScale;
        if (this.includeUnaccounted) {
          fitScale = sumRawPct > 100 ? 100 / sumRawPct : 1; // clamp overflow
        } else {
          fitScale = sumRawPct > 0 ? 100 / sumRawPct : 0; // force ML-only to 100%
        }
        fitScaleArr.push(fitScale);

        // displayed percentages (always normalised in % view)
        const scaleForPct = this.includeUnaccounted
          ? sumRawPct > 100
            ? 100 / sumRawPct
            : 1
          : sumRawPct > 0
            ? 100 / sumRawPct
            : 0;

        classes.forEach((c) => {
          const rawPct = pctRawByClass[c][pctRawByClass[c].length - 1][1];
          pctDisplayByClass[c].push([ts, rawPct * scaleForPct]);
        });

        unaccountedPctDisplay.push([
          ts,
          this.includeUnaccounted
            ? Math.max(100 - sumRawPct * scaleForPct, 0)
            : 0,
        ]);

        if (typeof tot_dB === "number") {
          totALine.push([ts, tot_dB]);
        } else if (E_total > 0) {
          totALine.push([ts, 10 * Math.log10(E_total)]);
        } else {
          totALine.push([ts, null]);
        }

        diagInfo.push({ underPct, overPct, sumRawPct });
      }

      return {
        classes,
        pctDisplayByClass,
        pctRawByClass,
        unaccountedPctDisplay,
        totALine,
        laeqByClass,
        diagInfo,
        fitScaleArr,
      };
    },

    buildContributionSeries() {
      const {
        classes,
        pctDisplayByClass,
        unaccountedPctDisplay,
        totALine,
        laeqByClass,
        fitScaleArr,
      } = this.computeMlContribOverTime();

      const UNACCOUNTED_NAME = "Unaccounted for Noise";
      const UNACCOUNTED_COLOR = "#9e9e9e";
      const colorFor = (name) =>
        name === UNACCOUNTED_NAME
          ? UNACCOUNTED_COLOR
          : this.colorForSeries(name);

      const areaSeries = [];

      if (!this.overlayPredictions) {
        // PERCENT VIEW (always normalised): areas = (% of total) × LAeq_total
        const toScaledArea = (pairs) =>
          pairs.map(([ts, pct], idx) => {
            const tot = totALine[idx]?.[1];
            return [ts, typeof tot === "number" ? (pct / 100) * tot : null];
          });

        this.getAreaDrawOrder(classes, laeqByClass).forEach((cls) => {
          areaSeries.push({
            id: `ml:${cls}`, // <— stable id
            name: cls,
            type: "line",
            stack: "scaled_dba",
            areaStyle: { opacity: 0.6 },
            symbol: "none",
            showSymbol: false,
            smooth: false,
            yAxisIndex: 0,
            data: toScaledArea(pctDisplayByClass[cls] || []),
            lineStyle: { width: 1, color: colorFor(cls) },
            itemStyle: { color: colorFor(cls) },
            z: 1,
            _isContrib: true,
          });
        });

        if (this.includeUnaccounted) {
          areaSeries.push({
            name: UNACCOUNTED_NAME,
            type: "line",
            stack: "scaled_dba",
            areaStyle: { color: { image: this.createHatchPattern(), repeat: "repeat" }, opacity: 0.6 },
            symbol: "none",
            showSymbol: false,
            smooth: false,
            yAxisIndex: 0,
            data: toScaledArea(unaccountedPctDisplay),
            lineStyle: { width: 1, color: UNACCOUNTED_COLOR },
            itemStyle: { color: UNACCOUNTED_COLOR },
            z: 1,
            _isContrib: true,
          });
        }
      } else {
        // OVERLAY LAeq VIEW (optionally normalised)
        const drawOrder = this.getAreaDrawOrder(classes, laeqByClass);
        const dbShift = fitScaleArr.map((f) =>
          f > 0 ? 10 * Math.log10(f) : -Infinity
        );

        drawOrder.forEach((cls) => {
          const data = (laeqByClass[cls] || []).map(([ts, dB], idx) => {
            if (dB == null || !isFinite(dB)) return [ts, null];
            if (!this.normalizePercentages) return [ts, dB];
            const s = dbShift[idx];
            if (!isFinite(s)) return [ts, null];
            return [ts, dB + s];
          });
          areaSeries.push({
            name: cls,
            type: "line",
            areaStyle: { opacity: 0.35 },
            symbol: "none",
            showSymbol: false,
            smooth: false,
            yAxisIndex: 0,
            data,
            lineStyle: { width: 1, color: colorFor(cls) },
            itemStyle: { color: colorFor(cls) },
            z: 1,
            _isContrib: true,
          });
        });

        if (this.includeUnaccounted) {
          // remaining (% of total) × LAeq_total
          const toScaledArea = (pairs) =>
            pairs.map(([ts, pct], idx) => {
              const tot = totALine[idx]?.[1];
              return [ts, typeof tot === "number" ? (pct / 100) * tot : null];
            });
          areaSeries.push({
            name: UNACCOUNTED_NAME,
            type: "line",
            areaStyle: { color: { image: this.createHatchPattern(), repeat: "repeat" }, opacity: 0.6 },
            symbol: "none",
            showSymbol: false,
            smooth: false,
            yAxisIndex: 0,
            data: toScaledArea(unaccountedPctDisplay),
            lineStyle: { width: 1, color: UNACCOUNTED_COLOR },
            itemStyle: { color: UNACCOUNTED_COLOR },
            z: 1,
            _isContrib: true,
          });
        }
      }

      const legendDataAdd = this.includeUnaccounted ? [UNACCOUNTED_NAME] : [];
      const legendSelectedAdd = this.includeUnaccounted
        ? { [UNACCOUNTED_NAME]: true }
        : {};

      return { areaSeries, legendDataAdd, legendSelectedAdd };
    },

    updateMlContribOptions() {
      if (!this.timeseriesChartOptions || !this.timeseriesChartOptions.series)
        return;

      // Remove any previous contribution series
      const baseSeries = (this.timeseriesChartOptions.series || []).filter(
        (s) => !s._isContrib
      );

      // If toggle OFF, remove ML contribution areas and their legend entries
      if (!this.showMlPredictions) {
        const prevLegend = this.timeseriesChartOptions.legend || {};
        const mlNames = [
          ...(this.predictionSeriesNames || []),
          "Unaccounted for Noise",
        ];

        const nextLegendData = (prevLegend.data || []).filter(
          (n) => !mlNames.includes(n)
        );
        const nextSelected = { ...(prevLegend.selected || {}) };
        mlNames.forEach((n) => {
          delete nextSelected[n];
        });

        this.timeseriesChartOptions = {
          ...this.timeseriesChartOptions,
          legend: {
            ...prevLegend,
            data: nextLegendData,
            selected: nextSelected,
          },
          series: baseSeries,
        };
        return; // <<< done
      }

      // Toggle ON → build (or rebuild) ML contribution area
      this.ensurePredictionColors();

      // Build new contribution areas
      const { areaSeries, legendDataAdd } = this.buildContributionSeries();

      // Merge legend entries (keep existing)
      const prevLegend = this.timeseriesChartOptions.legend || {};
      const nextLegendData = Array.from(
        new Set([...(prevLegend.data || []), ...legendDataAdd])
      );
      const nextLegendSelected = { ...(prevLegend.selected || {}) };

      this.timeseriesChartOptions = {
        ...this.timeseriesChartOptions,
        legend: {
          ...prevLegend,
          data: nextLegendData,
          selected: nextLegendSelected,
        },
        series: [...baseSeries, ...areaSeries],
      };
    },

    assignColor(name) {
      // Don’t assign palette colors to non-ML lines
      if (name === "TOT A" || name === FILTERED_LEVELS_NAME) return null;

      const used = new Set(Object.values(this.seriesColorMap));
      const palette = this.colorPalette;
      const next =
        palette.find((c) => !used.has(c)) ||
        palette[Object.keys(this.seriesColorMap).length % palette.length];
      this.seriesColorMap[name] = next;
      return next;
    },
    ensurePredictionColors() {
      // Ensure every ML series (from backend + ml) has a color
      (this.predictionSeriesNames || []).forEach((name) => {
        if (!this.seriesColorMap[name]) this.assignColor(name);
      });
    },
    colorForSeries(name) {
      if (name === "TOT A") return "#000";
      if (name === FILTERED_LEVELS_NAME) return "#000";
      return this.seriesColorMap[name] || this.assignColor(name) || "#7f7f7f";
    },
    createHatchPattern() {
      if (this.hatchPatternCanvas) return this.hatchPatternCanvas;
      const size = 10;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, size, size);
      ctx.strokeStyle = "#9e9e9e";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, size);
      ctx.lineTo(size, 0);
      ctx.stroke();
      this.hatchPatternCanvas = canvas;
      return canvas;
    },

    getZoomIndices() {
      const startMs =
        this.sharedZoom?.minTime ?? new Date(this.xArray[0]).getTime();
      const endMs =
        this.sharedZoom?.maxTime ??
        new Date(this.xArray[this.xArray.length - 1]).getTime();
      let s = 0,
        e = this.xArray.length - 1;
      for (let i = 0; i < this.xArray.length; i++) {
        const t = new Date(this.xArray[i]).getTime();
        if (t >= startMs) {
          s = i;
          break;
        }
      }
      for (let i = s; i < this.xArray.length; i++) {
        const t = new Date(this.xArray[i]).getTime();
        if (t > endMs) {
          e = Math.max(s, i - 1);
          break;
        }
      }
      return [s, e];
    },
    avgInRange(pairs, s, e) {
      let sum = 0,
        n = 0;
      for (let i = s; i <= e && i < pairs.length; i++) {
        const v = pairs[i]?.[1];
        if (typeof v === "number" && isFinite(v)) {
          sum += v;
          n++;
        }
      }
      return n ? sum / n : -Infinity;
    },
    getAreaDrawOrder(classes, laeqByClass) {
      // Compute an average LAeq metric per class over current zoom window
      const [s, e] = this.getZoomIndices();
      const metric = {};
      classes.forEach((c) => {
        metric[c] = this.avgInRange(laeqByClass[c] || [], s, e);
      });

      // Preferred top/front order for these three
      const preferredTop = ["Mine Noise", "Train Noise", "Road Noise"];
      const presentTop = preferredTop.filter((c) => classes.includes(c));
      const others = classes.filter((c) => !presentTop.includes(c));

      const orderMode = this.areaOrderMode;
      const cmpAsc = (a, b) => metric[a] - metric[b];
      const cmpDesc = (a, b) => metric[b] - metric[a];

      let orderedOthers;
      if (orderMode === "alphabetical") {
        orderedOthers = others.slice().sort((a, b) => a.localeCompare(b));
      } else if (orderMode === "largest-first") {
        // Largest should be in front/top ⇒ draw smaller first (so largest last)
        orderedOthers = others.slice().sort(cmpAsc);
      } else if (orderMode === "smallest-first") {
        // Smallest in front/top ⇒ draw larger first (so smallest last)
        orderedOthers = others.slice().sort(cmpDesc);
      } else {
        // Default: within "others", lowest values in front/top
        orderedOthers = others.slice().sort(cmpDesc); // draw larger first, lowest last in this group
      }

      // Draw order semantics: later series are drawn on top/front.
      // We want the preferredTop to be the ultimate front in the order Mine > Train > Road.
      // Since later is front, append in reverse so Mine ends up last (topmost).
      const preferredDraw = presentTop.slice().reverse(); // ["Road Noise","Train Noise","Mine Noise"]

      // Final drawing order
      return [...orderedOthers, ...preferredDraw];
    },
  },
  computed: {
    isAdmin() {
      try {
        return this.profile?.keycloak?.hasRealmRole("internal_realm_admin_role") ?? false;
      } catch (e) {
        console.warn("Could not detect roles from token:", e);
        return false;
      }
    },
  },
  mounted() {
    if (this.initialParams && Object.keys(this.initialParams).length > 0) {
      this.selectedSite = this.initialParams.site;
      this.selectedUnit = `${this.initialParams.unit_ID}`;
      this.date = this.roundToNearest15Min(
        new Date(this.initialParams.date_time)
      );
      const localDate = new Date(
        this.date.getTime() - this.date.getTimezoneOffset() * 60000
      );
      this.APIparams = {
        site: this.selectedSite,
        unit_ID: this.selectedUnit,
        date_time: localDate.toISOString(),
      };
    }
    this.startup();
    this._onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.prepareInference();
    };
    document.addEventListener("visibilitychange", this._onVisibilityChange);
  },
  watch: {
    // Watches profile (not just mounted) so this re-fires once Keycloak auth resolves asynchronously.
    profile: {
      immediate: true,
      handler(newProfile) {
        if (newProfile?.isAuthenticated?.value) this.prepareInference();
      },
    },
    initialParams(newParams) {
      this.selectedSite = newParams.site;
      this.selectedUnit = newParams.unit_ID;
      this.date = newParams.date_time;
      const localDate = new Date(
        this.date.getTime() - this.date.getTimezoneOffset() * 60000
      );
      this.APIparams = {
        site: this.selectedSite,
        unit_ID: this.selectedUnit,
        date_time: localDate.toISOString(),
      };
    },
    selectedSite(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.APIparams.site = newVal;
        this.fetchAvailableUnits();
        this.prepareInference();
      }
    },
    selectedUnit(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.APIparams.unit_ID = newVal;
      }
    },
    date(newVal, oldVal) {
      if (newVal.getTime() !== oldVal.getTime()) {
        this.APIparams.date_time = new Date(
          this.date.getTime() - this.date.getTimezoneOffset() * 60000
        ).toISOString();
      }
    },
    sharedZoom(newZoom) {
      if (newZoom && this.timeseriesChartOptions) {
        this.timeseriesChartOptions.dataZoom[0].startValue = newZoom.minTime;
        this.timeseriesChartOptions.dataZoom[0].endValue = newZoom.maxTime;
      }
      this.updateMlContribOptions();
    },
    chartView(view) {
      if (view === "Spectra") {
        this.updateSpectraChartOptions();
      }
    },
    range() {
      if (
        this.backendResponse &&
        this.timeseriesChartOptions &&
        this.timeseriesChartOptions.series
      ) {
        const newTrace = this.getFilteredLevelsTrace();
        const newData = newTrace.x.map((xi, idx) => [xi, newTrace.y[idx]]);
        const index = this.timeseriesChartOptions.series.findIndex(
          (serie) => serie.name === FILTERED_LEVELS_NAME
        );
        if (index !== -1) {
          this.timeseriesChartOptions.series[index].data = newData;
        } else {
          this.timeseriesChartOptions.series.push({
            name: newTrace.name,
            type: "line",
            data: newData,
            smooth: false,
            lineStyle: {
              type: "dashed",
              width: newTrace.line.width,
              color: "#ff7f0e",
            },
            itemStyle: {
              color: "#ff7f0e",
            },
          });
          this.timeseriesChartOptions.legend.data.push(newTrace.name);
        }
        this.timeseriesChartOptions = { ...this.timeseriesChartOptions };
      }
    },
    isZooming(newState) {
      if (newState == false) {
        this.handleDataZoom();
      }
    },
    audioTime(newTime) {
      const newDate = new Date(newTime);
      const floorDate = this.findFloorDate(this.xArray, newDate);
      if (!floorDate) return;
      if (
        this.timeseriesChartOptions &&
        this.timeseriesChartOptions.series &&
        this.timeseriesChartOptions.series.length > 0
      ) {
        this.timeseriesChartOptions.series[0].markLine = {
          silent: true,
          symbol: "none",
          animation: false,
          lineStyle: {
            color: "black",
            width: 2,
            type: "dashed",
          },
          data: [
            {
              xAxis: floorDate,
            },
          ],
          label: {
            show: false,
            // position: "end",
            // formatter: (params) => this.formatMarkLine(params.value),
          },
        };
        // this.timeseriesChartOptions = { ...this.timeseriesChartOptions };
      }
    },
    seriesVisibility: {
      deep: true,
      handler() {
        this.updateMlContribOptions();
      },
    },
    includeUnaccounted() {
      this.updateMlContribOptions();
    },
    overlayPredictions() {
      this.updateMlContribOptions();
    },
    normalizePercentages() {
      this.updateMlContribOptions();
    },
    areaOrderMode() {
      this.updateMlContribOptions();
    }
  },
  setup() {
    const splChartRef = ref(null);
    return { splChartRef };
  },
  async beforeUnmount() {
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    await this.handleUnmount();
  },
};
</script>

<style>
.spl-vue-container {
  height: 100%;
  display: grid;
  grid-template-rows: var(--toolbar-height) var(--info-bar-height) 1fr;
}

.selections {
  display: flex;
  padding: var(--spacing-xs);
  margin-right: var(--spacing-lg);
  font-size: 1.2em;
  background-color: var(--colour-bg-light);
  justify-content: space-around;
}

.selections .p-select,
.p-datepicker {
  margin-right: var(--spacing-ml);
}

.page-selection button {
  margin-left: 10px;
  margin-right: 10px;
}

.selections-and-help {
  height: var(--toolbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--colour-bg-light);
  width: 100%;
}

.button-group {
  display: flex;
  align-items: flex-end;
  max-height: 100%;
  gap: var(--spacing-sm);
}

.ml-toggle {
  cursor: pointer;
  margin-right: 15px;
}

.avatar {
  margin-right: 20px;
}

.info-bar {
  height: var(--info-bar-height);
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.status-container {
  display: flex;
  align-items: center;
  background-color: var(--colour-bg-light);
  padding: 0 var(--spacing-xs) 0 var(--spacing-xs);
  height: var(--info-bar-height);
  align-content: right;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-sm);
}

.status-item {
  display: flex;
  align-items: center;
  margin-right: var(--spacing-lg);
  gap: var(--spacing-xs);
}

.status-item-icon {
  width: var(--icon-size-lg);
}

.info-bar-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.sound-levels-page-container {
  width: 100%;
  height: 100%;
  /* overflow: auto;
  display: flex;
  flex-direction: column; */
  margin: 0 !important;
}

.sound-levels-app-container {
  width: 100%;
  height: 100%;
}

.pages-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.pages-wrapper h1 {
  justify-self: left;
  padding-left: var(--spacing-ml);
}

.analysis-page,
.factsheet-page {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.analysis-page {
  display: grid;
  grid-template-rows: 23.0625rem 1fr var(--toolbar-height);
}

.factsheet-page {
  height: 48.75rem;
}

.analysis-page:not(.active),
.factsheet-page:not(.active) {
  visibility: hidden;
  pointer-events: none;
}

.sound-levels-predictions-and-audio-container {
  width: 100%;
  display: grid;
  /* grid-template-columns: minmax(100px, 1fr) 5fr; */
  grid-template-columns: auto;
  grid-gap: 0px;
  background-color: var(--colour-bg-light);
  padding: 0;
  position: relative;
  box-sizing: border-box;
}

.spectra-chart-container-spl {
  position: absolute;
  height: 12.5rem;
  width: 100%;
  padding: var(--spacing-xs);
}

.spl-analysis {
  width: 100%;
  height: 100%;
  padding: 0;
  padding-left: var(--spacing-2xl);
  padding-right: var(--spacing-2xl);
  flex-direction: column;
  grid-gap: 0px;
  background-color: var(--colour-bg-light);
  position: relative;
  box-sizing: border-box;
  overflow: auto;
}

.spl-controls-container {
  width: 100%;
  display: grid;
  grid-template-columns: 2fr 8fr;
  align-items: center;
}

.v-chart,
.lifetime-waveform {
  width: 100%;
  padding: 0px;
}

.plot-class {
  display: flex;
  align-items: center;
  justify-content: center;
  height: minmax(100%, 400px);
  padding: 0;
  font-weight: bold;
  font-size: 0.9em;
  color: black;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
  box-sizing: border-box;
}

.cam-plot-loading,
.loading-indicator {
  text-align: center;
  font-size: 1em;
  margin: var(--spacing-md) 0;
  color: var(--colour-text-dark);
  height: 0;
}

.ml-status {
  height: auto;
  z-index: 1;
  align-content: left;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-xs);
  display: flex;
}

.check {
  color: green;
}

@media (max-width: 768px) {
  .sound-levels-predictions-and-audio-container {
    grid-template-columns: 1fr;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-selection {
  margin-top: var(--spacing-xs);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: var(--spacing-sm);
  width: max-content;
  margin-left: auto;
  margin-right: auto;
}

.icon-button {
  cursor: pointer;
  border: none;
  background: none;
  padding: 0 var(--spacing-md);
}

.icon-cog {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.8rem;
  width: 2.8rem;
  padding: 0;
  margin-right: var(--spacing-ml);
  cursor: pointer;
}

.icon-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.page-selection button:disabled {
  background-color: var(--color-primary-disabled);
  color: var(--colour-bg-light);
  cursor: not-allowed;
  opacity: 0.6;
}

.controls-dropdown {
  position: relative;
  cursor: pointer;
}

.controls-dropdown .cog {
  font-size: 1.2em;
}

.controls-menu {
  position: absolute;
  right: 1rem;
  top: 4.6rem;
  background: white;
  border: 1px solid var(--colour-border);
  padding: 0.5em;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  width: 25rem;
}

.controls-menu .menu-section {
  margin-bottom: 0.5em;
}

.controls-menu label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.25em;
}

.controls-menu button {
  display: block;
  width: 90%;
  margin-bottom: 0.25em;
  padding: 0.25em;
  background: var(--colour-bg-light);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.controls-menu button:hover {
  background: var(--colour-border-light);
}

.radio-row {
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-sm);
}

.radio-row label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: normal;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0;
  cursor: pointer;
  margin-left: 0.5rem;
  /*This is a bit of a bandaid, since we're moving moving these into the analysis settings dropdown soon*/
}

.controls-select {
  width: 100%;
  padding: 4px;
}

.spl-spinner {
  width: 60%;
  height: 60%;
}
</style>
